/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { useParams } from 'react-router';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconFeedback, IconLogout } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { useGetStripeSessionQuery, useUpdateStripeSuccessMutation } from '../api/UserLedgerAPI.js';
import { StripePaymentResponse } from 'model';
import { QuirkLoading } from '../components/QuirkLoading.js';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/fetchBaseQuery.js';
import { IonCard, IonCardContent, IonCardHeader, IonCardSubtitle, IonCardTitle, IonToast } from '@ionic/react';
import { pluralize } from '../util/pluralize.js';
import { NavLink } from 'react-router-dom';
import { LabelValue } from '../components/Forms/LabelValue.js';
import styles from './StripePaymentHandler.module.css';
import classNames from 'classnames';
import { useEffectOnce } from '../hooks/useEffectOnce.js';

export const StripePaymentHandler: React.FC = () => {
	const paymentSession = useParams<StripePaymentResponse & { status: string }>();

	const [updateStripeSuccess, { isError, isLoading, isSuccess, data: userLedger, error }] =
		useUpdateStripeSuccessMutation();
	const { stripeSession, userLedgerId } = paymentSession;
	const { data: session } = useGetStripeSessionQuery(paymentSession.stripeSession);
	useEffectOnce(() => {
		if (stripeSession && userLedgerId && !isLoading && updateStripeSuccess) {
			updateStripeSuccess({
				stripeSession,
				userLedgerId
			});
		}
		// We don't want to re-render every time the updateStripeSuccess function changes, and we know it will be here on first render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stripeSession, userLedgerId]);

	let quantity = 0;
	let tax = 0;
	let subtotal = 0;
	let total = 0;
	const item = (session?.line_items as any)?.data[0];
	if (item && item.quantity) {
		quantity = Number(item.quantity);
	}
	if (item && item.amount_tax) {
		tax = Number(item.amount_tax);
	}
	if (item && item.amount_subtotal) {
		subtotal = Number(item.amount_subtotal);
	}
	if (item && item.amount_total) {
		total = Number(item.amount_total);
	}
	const complete = userLedger && userLedger.transactionStatus === 'completed';

	return (
		<QuirkIonPage roles={['user']} name="Success!">
			<Header title={'Payment results'} />
			<QuirkIonContent flush>
				<>
					<QuirkLoading isLoading={isLoading} message="Updating payment..." />
					{isSuccess && userLedger && session && complete && (
						<>
							<IonCard>
								<IonCardHeader>
									<IonCardTitle data-testid="stripeTitle">Success</IonCardTitle>
									<IonCardSubtitle data-testid="stripeSubTitle">
										{userLedger.account === 'token' &&
											`${userLedger.amount} ${pluralize(quantity, 'Token')} transferred`}
										{userLedger.account === 'subscription' && item.description}
									</IonCardSubtitle>
								</IonCardHeader>
								<IonCardContent>
									<div style={{ width: 200 }}>
										<LabelValue label="Subtotal" value={subtotal / 100} currency />
										<LabelValue label="Tax" value={tax / 100} currency />
										<LabelValue label="Total" value={total / 100} currency />
									</div>
								</IonCardContent>
							</IonCard>
						</>
					)}
					{paymentSession.status !== 'cancel' && isError && (
						<IonCard>
							<IonCardHeader>
								<IonCardTitle>Something went wrong</IonCardTitle>
							</IonCardHeader>
							<IonCardContent>
								<div>{(error as FetchBaseQueryError)?.data as string}</div>
								<div className="space10" />
								<div>
									<NavLink to={`/main/settings/about/sendfeedback/`} className="nounderline">
										<IconButton label="Contact us" icon={<IconFeedback />} />
									</NavLink>
								</div>
							</IonCardContent>
						</IonCard>
					)}
					{paymentSession.status !== 'cancel' && !complete && isSuccess && (
						<IonCard>
							<IonCardHeader>
								<IonCardTitle>Something went wrong</IonCardTitle>
							</IonCardHeader>
							<IonCardContent>
								<div>The transaction was not completed, please contact the administrator</div>
								<div className="space10" />
								<div>
									<NavLink to={`/main/settings/about/sendfeedback/`} className="nounderline">
										<IconButton label="Contact us" icon={<IconFeedback />} />
									</NavLink>
								</div>
							</IonCardContent>
						</IonCard>
					)}
					{paymentSession.status === 'cancel' && !complete && (
						<IonCard>
							<IonCardHeader>
								<IonCardTitle>You've canceled this purchase</IonCardTitle>
							</IonCardHeader>
							<IonCardContent>
								<div>The transaction was canceled</div>
								<div className="space10" />
								<div>
									<NavLink to={`/main/settings/about/sendfeedback/`} className="nounderline">
										<IconButton label="Contact us" icon={<IconFeedback />} />
									</NavLink>
								</div>
							</IonCardContent>
						</IonCard>
					)}
					<IconButton
						color="primary"
						fill="outline"
						className={classNames('radius8', styles.close)}
						label="Close this page"
						onClick={() => window.close()}
						icon={<IconLogout />}
					/>
				</>
			</QuirkIonContent>
			<IonToast
				isOpen={isError && paymentSession.status !== 'cancel'}
				duration={1500}
				message={(error as FetchBaseQueryError)?.data as string}
				position="top"
				color="danger"
			/>
		</QuirkIonPage>
	);
};
